import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { memo, useEffect, useState } from 'react';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { SearchTextField } from 'src/components/SearchTextField';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { Theme, useTheme } from 'src/components/muiTheme/utils';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { isTrainer } from 'src/models/user/utils';
import { GlobalSearchDialog } from 'src/shared/GlobalSearch/GlobalSearchDialog';

import { ActionsMenuDreamcoat } from './ActionsMenuDreamcoat';
import { DreamcoatNavToggle } from './DreamcoatNavToggle';

export interface TopBarProps {
  isAdmin?: boolean;
  bgcolor: string;
}

export const TopBarDreamcoat: React.FC<TopBarProps> = memo(
  ({ isAdmin, bgcolor }) => {
    const { session, user } = useOptionalSession();
    const theme = useTheme();
    const mdDown = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down('md')
    );

    const [searchOpen, setSearchOpen] = useState(false);

    // Keyboard shortcut: Cmd+K (Mac) or Ctrl+K (Windows)
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          if (user && isTrainer(user) && !isAdmin) {
            setSearchOpen(true);
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [user, isAdmin]);

    // We need to take a bgcolor string like "background.level1"
    // and turn it into an actual palette variable by splitting it
    // so we can find the matching channel variable
    const bgcolorSegments = bgcolor?.split('.');
    const bgVar =
      bgcolorSegments.length === 2 &&
      theme.vars.palette[bgcolorSegments[0]] &&
      theme.vars.palette[bgcolorSegments[0]][bgcolorSegments[1] + 'Channel'];

    return (
      <>
        <Box
          sx={{
            position: 'sticky',
            top: '0px',
            height: '56px',
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: (theme) =>
              bgVar ||
              `rgba(${theme.vars.palette.background.defaultChannel} / 0.7)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            zIndex: theme.zIndex?.appBar,
            backdropFilter: 'blur(10px)',
            transition: 'background-color 0.5s',
            width: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              color="textSecondary"
              variant="h4"
              fontWeight={400}
              noWrap
              sx={{
                minWidth: 0,
              }}
            >
              {session?.brand}
            </Typography>
            {user && isTrainer(user) && !isAdmin && (
              <Box sx={{ mr: 2 }}>
                {mdDown ? (
                  <IconButton
                    onClick={() => setSearchOpen(true)}
                    variant={'tertiary'}
                  >
                    <Icon
                      size="small"
                      color="neutral"
                      icon={faMagnifyingGlass}
                    />
                  </IconButton>
                ) : (
                  <SearchTextField
                    value={''}
                    onFocus={() => setSearchOpen(true)}
                    onCancel={() => {}}
                    onSearch={() => {
                      setSearchOpen(true);
                    }}
                  />
                )}
              </Box>
            )}
          </Box>

          {!isAdmin && (
            <Stack direction="row" alignItems="center" gap={1}>
              <DreamcoatNavToggle />
              <ActionsMenuDreamcoat />
            </Stack>
          )}
        </Box>
        {user && session && isTrainer(user) && !isAdmin && (
          <GlobalSearchDialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
          />
        )}
      </>
    );
  }
);

TopBarDreamcoat.displayName = 'TopBarDreamcoat';
